// DP263 — the public page behind a shared site-visit photo. Standalone: no app
// chrome, no authenticated call, and it reads nothing but the token endpoint,
// which returns the snapshot taken at share time (later caption edits do not
// travel). The link ImageActionModal copies finally lands somewhere.
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Camera, ImageOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const prettyDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString([], { year: 'numeric', month: 'long', day: 'numeric' });
};

export default function PhotoShareViewer() {
  const { token } = useParams();
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/photo-shares/${token}`);
        if (live) setShare(r.data);
      } catch (err) {
        const code = err.response?.status;
        if (live) {
          if (code === 410) setError('This photo link has expired. Ask whoever sent it for a fresh one.');
          else if (code === 404) setError('This photo link is no longer available. It may have been revoked.');
          else setError('This photo could not be loaded. Please try again shortly.');
        }
      } finally {
        if (live) setLoading(false);
      }
    };
    if (token) load();
    return () => { live = false; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50" data-testid="photo-share-loading">
        <p className="text-sm text-stone-500">Loading the photo…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 px-6" data-testid="photo-share-error">
        <div className="max-w-md text-center">
          <ImageOff className="w-10 h-10 mx-auto mb-4 text-stone-400" aria-hidden="true" />
          <h1 className="text-lg font-semibold text-stone-800 mb-2">Photo unavailable</h1>
          <p className="text-sm text-stone-600" data-testid="photo-share-error-text">{error}</p>
          <p className="text-xs text-stone-400 mt-8">Shared from KEY-MADE</p>
        </div>
      </div>
    );
  }

  const photo = share?.photo || {};
  const project = share?.project || {};
  const visit = share?.visit || {};
  const url = photo.url || photo.image_url || photo.thumbnail_url;
  const visitLine = [visit.name || (visit.visit_number ? `Visit #${visit.visit_number}` : ''), prettyDate(visit.date)]
    .filter(Boolean).join(' · ');

  return (
    <div className="min-h-screen bg-stone-50" data-testid="photo-share-page">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-12">
        {project.name && (
          <p className="text-xs uppercase tracking-widest text-stone-500 mb-2" data-testid="photo-share-project">{project.name}</p>
        )}
        {visitLine && (
          <p className="flex items-center gap-2 text-sm text-stone-600 mb-6" data-testid="photo-share-visit">
            <Calendar className="w-4 h-4" aria-hidden="true" />{visitLine}
          </p>
        )}

        {url ? (
          <img src={url} alt={photo.caption || 'Shared photo'} className="w-full rounded-lg bg-stone-200"
            data-testid="photo-share-image" />
        ) : (
          <div className="w-full rounded-lg bg-stone-200 py-24 text-center text-sm text-stone-500" data-testid="photo-share-image-missing">
            The image could not be displayed.
          </div>
        )}

        {photo.caption && (
          <p className="mt-4 text-base text-stone-800" data-testid="photo-share-caption">{photo.caption}</p>
        )}

        {share?.sender_note && (
          <div className="mt-6 border-l-2 border-stone-300 pl-4" data-testid="photo-share-note">
            <p className="text-sm text-stone-700 whitespace-pre-wrap">{share.sender_note}</p>
          </div>
        )}

        <div className="mt-10 flex items-center gap-2 border-t border-stone-200 pt-4 text-xs text-stone-500">
          <Camera className="w-4 h-4" aria-hidden="true" />
          <span data-testid="photo-share-by">
            Shared from KEY-MADE{share?.created_by_name ? ` by ${share.created_by_name}` : ''}
            {share?.created_at ? ` · ${prettyDate(share.created_at)}` : ''}
          </span>
        </div>
      </div>
    </div>
  );
}
